import { useState } from "react"
import { useProjectByTenantQuery } from "@/v2-backoffice-client/project/byTenant/project.byTenant.query"
import { useTenantDefaultsQuery } from "@/v2-backoffice-client/tenant/defaults/tenant.defaults.query"

/** The wire shape of one member-defaults write (TenantDefaultsInput). */
export type TenantDefaultsValues = {
  TenantId: number
  // BigInt project id as a string (codegen shape); null clears the default.
  ProjectId: string | null
  // Dropped by the server when ProjectId is set — a default project carries
  // its own country. Null clears a standalone default country.
  CountryCode: number | null
}

export type TenantDefaultsContext = ReturnType<typeof useTenantDefaultsValues>

/**
 * A team's member defaults (project + country) as one state machine, with
 * the same project-settles-the-country rule the server applies on write.
 *
 * The stored defaults are fetched here rather than taken from the caller:
 * they cost the API a lookup that the tenant list has no use for, and
 * fetching on mount is what lets the row menu open the same dialog as the
 * team panel. Because they arrive async, the operator's edits overlay them:
 * `undefined` means untouched (show the server's answer once it lands), a
 * value is the operator's choice — so there is no seeding step for a slow
 * query to race.
 */
export function useTenantDefaultsValues(tenantId: number) {
  const [projectEdit, setProjectEdit] = useState<string | null>()
  const [countryEdit, setCountryEdit] = useState<number | null>()

  const {
    data: defaultsData,
    loading: defaultsLoading,
    error: queryError,
  } = useTenantDefaultsQuery({
    variables: { id: tenantId },
  })
  const defaults = defaultsData?.getTenantById

  const { data: projectData, loading: projectsLoading } =
    useProjectByTenantQuery({
      variables: { tenantId },
    })
  const projects = projectData?.getProjectsByTenantId ?? []

  const projectId =
    projectEdit !== undefined
      ? projectEdit
      : (defaults?.DefaultProjectId ?? null)
  const countryCode =
    countryEdit !== undefined
      ? countryEdit
      : (defaults?.DefaultCountryCode ?? null)

  // A default that no longer resolves to one of the team's projects — it
  // was deleted, or unlinked — is no default at all. That is how the
  // backend reads it when someone joins, so the fields show the same
  // thing, and saving is what clears the dangling id.
  const projectsResolved = !projectsLoading && projectData !== undefined
  const staleProject =
    projectId !== null &&
    projectsResolved &&
    !projects.some((p) => p.Id === projectId)
  const effectiveProjectId = staleProject ? null : projectId

  // A project settles the country by itself, so offering both would let
  // the operator save two answers to one question. SetTenantDefaults drops
  // the country for a project anyway — the disabled picker shows the same
  // rule the server applies.
  const countryFollowsProject = effectiveProjectId !== null
  const effectiveCountryCode = countryFollowsProject ? null : countryCode

  const pending = defaultsLoading || projectsLoading

  // Only when actually changed: the defaults mutation rewrites the team's
  // configuration blob, which a rename has no business touching. Nothing to
  // write while the defaults are still loading, when the form holds no
  // answer to compare against.
  const changed =
    !!defaults &&
    (effectiveProjectId !== (defaults.DefaultProjectId ?? null) ||
      effectiveCountryCode !== (defaults.DefaultCountryCode ?? null))

  const values: TenantDefaultsValues | null = changed
    ? {
        TenantId: tenantId,
        ProjectId: effectiveProjectId,
        CountryCode: effectiveCountryCode,
      }
    : null

  return {
    projects,
    pending,
    queryError,
    effectiveProjectId,
    setProjectId: setProjectEdit,
    staleProject,
    effectiveCountryCode,
    setCountryCode: setCountryEdit,
    countryFollowsProject,
    values,
  }
}
